import {
	BookOpen,
	ClipboardList,
	Copy,
	Download,
	Edit,
	Folder,
	MoreVertical,
	Share2,
	Star,
	Trash2,
	Circle,
	Users,
	Box,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { SelectButton } from '@/components/ui/SelectButton'
import type { DocumentPreviewData, ContentType, PlayPreview } from '@/types/Content.ts'
import { PlaybookPreviewGrid } from './PlaybookPreviewGrid'
import { DocumentPreviewGrid } from './DocumentPreviewGrid'
import './content-card.css'
import React from 'react'

const typeIcons: Record<ContentType, React.ComponentType<{
	size?: number;
	className?: string;
	strokeWidth?: number
}>> = {
	playbook: BookOpen,
	document: ClipboardList,
	folder: Folder,
	play: Circle,
	formation: Users,
	concept: Box,
}

type ContentCardProps = {
	id: string
	name: string
	type: ContentType
	playCount?: number
	sectionCount?: number
	sourcePlaybookName?: string
	lastModified: string
	thumbnail?: string
	previewPlays?: PlayPreview[]
	previewDocument?: DocumentPreviewData
	isStarred?: boolean
	dataAnimate?: 'reveal' | 'fade-slide'
	style?: React.CSSProperties
	selected?: boolean
	onSelect?: (id: string, type: ContentType) => void
	onRename: (id: string) => void
	onDelete: (id: string) => void
	onDuplicate: (id: string) => void
	onExport?: (id: string) => void
	onShare?: (id: string) => void
	onToggleStar?: (id: string) => void
}

export const ContentCard = React.memo(function ContentCard({
								id,
								name,
								type,
								playCount = 0,
								sectionCount = 0,
								sourcePlaybookName,
								lastModified,
								thumbnail,
								previewPlays,
								previewDocument,
								isStarred = false,
								dataAnimate,
								style,
								selected,
								onSelect,
								onRename,
								onDelete,
								onDuplicate,
								onExport,
								onShare,
								onToggleStar,
							}: ContentCardProps) {
	const navigate = useNavigate()
	const TypeIcon = typeIcons[type]

	const handleOpen = () => {
		if (type === 'playbook') {
			navigate(`/playbooks/${id}`)
		} else if (type === 'document') {
			navigate(`/documents/${id}/edit`)
		}
		// Folders handled differently - no navigation on click
	}

	const getMetaText = () => {
		switch (type) {
			case 'playbook':
				return `${playCount} ${playCount === 1 ? 'play' : 'plays'}`
			case 'document':
				return `${playCount} ${playCount === 1 ? 'play' : 'plays'} \u2022 ${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'}`
			case 'folder':
			case 'play':
			case 'formation':
			case 'concept':
				return ''
		}
	}

	const getSecondaryMetaText = () => {
		if (type === 'document' && sourcePlaybookName) {
			return `From: ${sourcePlaybookName}`
		}
		return null
	}

	return (
		<div
			className="content-card"
			data-animate={dataAnimate}
			style={style}
			onClick={handleOpen}
		>
			{/* Thumbnail/Preview */}
			<div className="content-card-thumbnail">
				{onSelect && (
					<SelectButton
						selected={selected}
						onClick={() => onSelect(id, type)}
						className="content-card-select-button"
					/>
				)}
				{type === 'playbook' && previewPlays && previewPlays.length > 0 ? (
					<PlaybookPreviewGrid
						previewPlays={previewPlays}
						playCount={playCount}
					/>
				) : type === 'document' && previewDocument ? (
					<DocumentPreviewGrid
						preview={previewDocument}
						playCount={playCount}
					/>
				) : thumbnail ? (
					<img src={thumbnail} alt={name} />
				) : (
					<div className="content-card-thumbnail-gradient">
						<TypeIcon className="content-card-thumbnail-icon" />
					</div>
				)}
			</div>

			{/* Card Info */}
			<div className="content-card-info">
				<div className="content-card-header">
					<div className="content-card-type-indicator">
						<TypeIcon size={14} strokeWidth={2} />
						<span className="content-card-type-label">
							{type === 'playbook' ? 'Playbook' : type === 'document' ? 'Document' : 'Folder'}
						</span>
					</div>

					<div className="content-card-actions">
						{(type === 'playbook' || type === 'document') && (
							<>
								{onToggleStar && (
									<button
										className="content-card-action-button content-card-action-button-star"
										onClick={(e) => {
											e.stopPropagation()
											onToggleStar(id)
										}}
										title={isStarred ? `Unstar ${type === 'document' ? 'Document' : 'Playbook'}` : `Star ${type === 'document' ? 'Document' : 'Playbook'}`}
									>
										<Star
											className={isStarred ? 'content-card-action-icon-starred' : 'content-card-action-icon'}
										/>
									</button>
								)}
								{onShare && (
									<button
										className="content-card-action-button content-card-action-button-share"
										onClick={(e) => {
											e.stopPropagation()
											onShare(id)
										}}
										title={`Share ${type === 'document' ? 'Document' : 'Playbook'}`}
									>
										<Share2 size={16} />
									</button>
								)}
							</>
						)}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									className="content-card-action-button content-card-action-button-more"
									onClick={(e) => {
										e.stopPropagation()
									}}
									title="More options"
								>
									<MoreVertical size={16} />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									handleOpen()
								}}>
									<BookOpen className="content-card-menu-icon" />
									Open
								</DropdownMenuItem>
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									onRename(id)
								}}>
									<Edit className="content-card-menu-icon" />
									Rename
								</DropdownMenuItem>
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									onDuplicate(id)
								}}>
									<Copy className="content-card-menu-icon" />
									Duplicate
								</DropdownMenuItem>
								{onExport && (
									<DropdownMenuItem onClick={(e) => {
										e.stopPropagation()
										onExport(id)
									}}>
										<Download className="content-card-menu-icon" />
										Export
									</DropdownMenuItem>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem
									variant="destructive"
									onSelect={(e) => {
										e.preventDefault()
										onDelete(id)
									}}
								>
									<Trash2 className="content-card-menu-icon" />
									Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<h3 className="content-card-title">{name}</h3>
				<p className="content-card-meta">
					{getMetaText()}
				</p>
				{getSecondaryMetaText() && (
					<p className="content-card-meta content-card-meta-source">
						{getSecondaryMetaText()}
					</p>
				)}
				<p className="content-card-meta">
					{lastModified}
				</p>
			</div>
		</div>
	)
})
